import { makeAutoObservable, runInAction } from 'mobx';
import { checkoutObservationEpoch } from '@/shared/utils/checkoutPaymentObservation';
import { captureMessage } from '@sentry/nextjs';
import { classifyWalletReadFailure, type WalletReadFailure } from '@/shared/utils/walletReadFailure';
import { CreateOrderUseCase } from '@/core/application/use-cases/order/CreateOrderUseCase';
import { IContainer } from '@/shared/config/Container';
import { PaymentType } from '@/models/payment-plan';
import { CreateCheckoutSessionUseCase } from '@/core/application/use-cases/checkout/CreateCheckoutSessionUseCase';
import {
  AvailablePaymentMethod,
  CheckoutSessionParams,
  CheckoutOrderResult,
  CheckoutSessionResult,
  CheckoutSessionService,
  CurrentCheckoutSession,
} from '@/core/infrastructure/services/ApiPaymentService';
import type {
  ApiPaymentService,
  WalletCredit,
  CheckoutConsentProvider,
  CheckoutConsentResult,
} from '@/core/infrastructure/services/ApiPaymentService';
import {
  PaymentPlanDetails,
  ProductPaymentPlanResponse,
} from '@/core/domain/entities/Product';
import { IProductRepository } from '@/core/domain/repositories/IProductRepository';
import { trackConsumerEvent } from '@/libs/analytics/consumerEvents';

export interface CheckoutItem {
  id: string;
  productId: string;
  productName: string;
  productImage: string;
  price: number;
  quantity: number;
  subtotal: number;
  selectedOptions?: Record<string, unknown>;
}

export interface PaymentBreakdown {
  id: string;
  price: number;
  plan_amount: number;
  payment_type: string;
  keepMyPrice: boolean;
  date: string;
  due_date?: string;
  installment_number?: number;
  total_installments?: number;
}

export interface PaymentPlan {
  type: PaymentType;
  totalAmount: number;
  frequency?: 'weekly' | 'monthly' | 'daily';
  duration?: number;
  installmentAmount?: number;
  totalInstallments?: number;
  processingFee?: number;
  breakdown?: PaymentBreakdown[];
  keepMyPrice?: boolean;
}

export interface WorkDetails {
  employerName: string;
  workAddress: string;
  position: string;
  salary: number;
  workPhone: string;
  employmentType: 'full-time' | 'part-time' | 'contract' | 'self-employed';
  yearsOfEmployment: number;
}

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  region: string;
  postalCode?: string;
  country: string;
  phone: string;
}

export interface CheckoutSummary {
  subtotal: number;
  shipping: number;
  tax: number;
  discount: number;
  processingFee: number;
  total: number;
  currency: string;
}

export interface CheckoutValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface CollectionPointOption {
  id: string;
  code: string;
  name: string;
  region: string;
  city: string;
  point_type: string;
  status: string;
  operating_hours: Record<string, unknown>;
  is_active: boolean;
  metadata: Record<string, unknown>;
}

interface HttpErrorLike {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: string;
      details?: Record<string, unknown>;
    };
  };
}

interface SelectedPlanLike {
  payment_option?: 'PSS' | 'PAY_IN_FULL';
}

export interface CheckoutBlocker {
  message: string;
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

class CheckoutStore {
  // Observable state
  isLoading = false;
  error: string | null = null;
  checkoutBlocker: CheckoutBlocker | null = null;
  checkoutLoadError: 'sign_in' | 'unavailable' | null = null;

  // Checkout data
  items: CheckoutItem[] = [];
  selectedPaymentPlan: PaymentPlan | null = null;
  selectedPaymentMethod: AvailablePaymentMethod | null = null;
  workDetails: WorkDetails | null = null;
  shippingAddress: ShippingAddress | null = null;
  checkoutSession: CheckoutSessionResult | null = null;
  currentCheckoutSession: CurrentCheckoutSession | null = null;
  // Checkout state
  termsAccepted = false;
  privacyAccepted = false;
  marketingOptIn = false;

  // Key Terms acceptance tracking
  keyTermsAcceptedAt: string | null = null;
  keyTermsVersion: string | null = null;

  // Order processing
  isCalculatingBreakdown = false;
  isCreatingOrder = false;
  isFinalizingOrder = false;
  createdOrder: CheckoutOrderResult | null = null;

  // Available options
  availablePaymentPlans: PaymentPlan[] = [];
  availableFrequencies: string[] = ['weekly', 'monthly'];

  paymentPlanResults: ProductPaymentPlanResponse | null = null;

  // Shopping Wallet state
  walletBalance = 0;
  walletCurrency = 'GHS';
  walletCredits: WalletCredit[] = [];
  walletApplication: { id: string; amount: number } | null = null;
  isLoadingWallet = false;
  isApplyingWallet = false;
  walletError: string | null = null;
  walletReadFailure: WalletReadFailure | null = null;
  walletConsent: CheckoutConsentResult | null = null;
  walletConsentError: 'setup_failed' | 'finalization_failed' | 'expired' | null = null;
  isRequestingWalletConsent = false;
  walletConsentPollingPaused = false;
  private checkoutGeneration = 0;
  private walletQuoteNeedsRefresh = false;
  private walletBalanceNeedsRefresh = false;
  private walletBalanceRequestVersion = 0;
  private consentRequestVersion = 0;
  private walletConsentRequest: Promise<boolean> | null = null;
  private walletConsentTimer: ReturnType<typeof setTimeout> | null = null;
  private walletConsentDeadline = 0;
  private walletPollGeneration = 0;
  private isPollingWalletConsent = false;

  // Delivery preferences state
  collectionPoints: CollectionPointOption[] = [];
  isLoadingCollectionPoints = false;
  collectionPointsError: string | null = null;

  // Use cases
  private createOrderUseCase: CreateOrderUseCase;
  private createCheckoutSessionUseCase: CreateCheckoutSessionUseCase;

  constructor(
    private container: IContainer,
    private readonly productRepository: IProductRepository,
    private readonly checkoutService: CheckoutSessionService,
    private readonly paymentService?: ApiPaymentService
  ) {
    makeAutoObservable(this);
    this.createOrderUseCase =
      container.get<CreateOrderUseCase>('CreateOrderUseCase');
    this.createCheckoutSessionUseCase =
      container.get<CreateCheckoutSessionUseCase>(
        'CreateCheckoutSessionUseCase'
      );
    this.initializeDefaultPlans();
  }

  // Computed properties
  get isSessionCleanupInProgress(): boolean {
    return Boolean(this.container.authStore?.isSigningOut);
  }

  get subtotal(): number {
    return this.items.reduce((sum, item) => sum + item.subtotal, 0);
  }

  get shipping(): number {
    // Free shipping for orders over GHS 500
    return this.subtotal >= 500 ? 0 : 50;
  }

  get tax(): number {
    // No tax for now, but could be calculated based on region
    return 0;
  }

  get discount(): number {
    // Could be calculated based on promo codes, user tier, etc.
    return 0;
  }

  get processingFee(): number {
    if (!this.selectedPaymentPlan || !this.selectedPaymentMethod) return 0;
    return this.selectedPaymentPlan.processingFee || 0;
  }

  get total(): number {
    return (
      this.subtotal +
      this.shipping +
      this.tax +
      this.processingFee -
      this.discount
    );
  }

  get checkoutSummary(): CheckoutSummary {
    return {
      subtotal: this.subtotal,
      shipping: this.shipping,
      tax: this.tax,
      discount: this.discount,
      processingFee: this.processingFee,
      total: this.total,
      currency: 'GHS',
    };
  }

  get itemCount(): number {
    return this.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  get canProceedToPayment(): boolean {
    return (
      this.items.length > 0 &&
      this.selectedPaymentPlan !== null &&
      this.selectedPaymentMethod !== null &&
      this.termsAccepted &&
      this.privacyAccepted &&
      (this.selectedPaymentPlan.type === PaymentType.FULL_PAYMENT ||
        this.workDetails !== null)
    );
  }

  get validationResult(): CheckoutValidation {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (this.items.length === 0) {
      errors.push('Cart is empty');
    }

    if (!this.selectedPaymentPlan) {
      errors.push('Payment plan not selected');
    }

    if (!this.selectedPaymentMethod) {
      errors.push('Payment method not selected');
    }

    if (!this.termsAccepted) {
      errors.push('Terms and conditions must be accepted');
    }

    if (!this.privacyAccepted) {
      errors.push('Privacy policy must be accepted');
    }

    if (
      this.selectedPaymentPlan?.type !== PaymentType.FULL_PAYMENT &&
      !this.workDetails
    ) {
      errors.push('Work details required for installment payments');
    }

    if (
      this.workDetails?.salary &&
      this.workDetails.salary < this.total * 0.3
    ) {
      warnings.push('Monthly payment may exceed recommended 30% of salary');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Whether home delivery option is available.
   * Currently returns false ("Coming Soon") - update when backend is ready.
   */
  get isDeliveryAvailable(): boolean {
    return false;
  }

  // Actions
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  clearCart(): void {
    runInAction(() => {
      this.items = [];
      this.selectedPaymentPlan = null;
      this.selectedPaymentMethod = null;
      this.checkoutBlocker = null;
    });
  }

  async getPaymentPlanOptions(productId: string): Promise<void> {
    try {
      this.setLoading(true);
      this.setError(null);
      this.checkoutBlocker = null;

      // Fetch payment plans (required) + product details (best-effort) in parallel.
      // Product details are used only to enrich the UI (brand/model/SKU) and should never
      // block checkout if the endpoint is unavailable.
      const [paymentPlans, productDetails] = await Promise.all([
        this.productRepository.getPaymentPlans(productId),
        this.productRepository.findById(productId).catch(() => null),
      ]);

      if (!paymentPlans) {
        throw new Error('Product not found');
      }

      runInAction(() => {
        // Merge brand_name and model_no from product details into payment plan results (when present)
        this.paymentPlanResults = {
          ...paymentPlans,
          product: {
            ...paymentPlans.product,
            brand_name:
              productDetails?.brand_name ?? paymentPlans.product.brand_name,
            model_no: productDetails?.model_no ?? paymentPlans.product.model_no,
          },
        };
      });
    } catch (error) {
      const httpError = error as HttpErrorLike;
      const responseData = httpError.response?.data;
      const statusCode =
        typeof httpError.response?.status === 'number'
          ? httpError.response.status
          : undefined;
      const message =
        typeof responseData?.message === 'string'
          ? responseData.message
          : error instanceof Error
            ? error.message
            : 'Failed to fetch product details';

      runInAction(() => {
        this.checkoutBlocker =
          typeof responseData?.code === 'string' || statusCode === 409
            ? {
                message,
                status: statusCode,
                code:
                  typeof responseData?.code === 'string'
                    ? responseData.code
                    : undefined,
                details:
                  responseData?.details && typeof responseData.details === 'object'
                    ? responseData.details
                    : undefined,
              }
            : null;
      });

      this.setError(message);
    } finally {
      this.setLoading(false);
    }
  }

  createCheckoutSession(productId: string, quantity: number): void {
    this.setLoading(true);
    this.setError(null);

    // This flow is not used in the current PaySmallSmall-first checkout,
    // but we explicitly avoid silently falling back to "pay in full".
    const paymentMethod = this.selectedPaymentMethod?.type;
    if (!paymentMethod) {
      this.setError('Select PaySmallSmall to continue');
      this.setLoading(false);
      return;
    }
    if (String(paymentMethod).toUpperCase() !== 'PSS') {
      this.setError('PaySmallSmall is required to continue');
      this.setLoading(false);
      return;
    }

    this.createCheckoutSessionUseCase
      .execute({
        productId,
        quantity,
        paymentMethod: 'PSS',
        userId: 'current_user_id', // Replace with actual user ID from auth store
      })
      .then((session) => {
        runInAction(() => {
          this.checkoutSession = session;
        });
      })
      .catch((error) => {
        this.setError(error.message || 'Failed to create checkout session');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  selectPaymentPlan(plan: PaymentPlan): void {
    runInAction(() => {
      this.selectedPaymentPlan = plan;
      // Reset payment method when plan changes
      this.selectedPaymentMethod = null;
    });
  }

  selectPaymentMethod(method: AvailablePaymentMethod | null): void {
    this.selectedPaymentMethod = method;
  }

  // Work details
  updateWorkDetails(details: Partial<WorkDetails>): void {
    runInAction(() => {
      this.workDetails = this.workDetails
        ? { ...this.workDetails, ...details }
        : (details as WorkDetails);
    });
  }

  // Shipping address
  updateShippingAddress(address: Partial<ShippingAddress>): void {
    runInAction(() => {
      this.shippingAddress = this.shippingAddress
        ? { ...this.shippingAddress, ...address }
        : (address as ShippingAddress);
    });
  }

  // Consent management
  acceptTerms(accepted: boolean): void {
    this.termsAccepted = accepted;
  }

  acceptPrivacy(accepted: boolean): void {
    this.privacyAccepted = accepted;
  }

  setMarketingOptIn(optIn: boolean): void {
    this.marketingOptIn = optIn;
  }

  /**
   * Records the user's acceptance of the Key Terms before order creation.
   * This is called when user taps "I Agree, Continue" in the KeyTermsSheet.
   */
  acceptKeyTerms(timestamp: string, version: string): void {
    runInAction(() => {
      this.keyTermsAcceptedAt = timestamp;
      this.keyTermsVersion = version;
    });
  }

  /**
   * Clears the key terms acceptance (e.g., when starting a new checkout).
   */
  clearKeyTermsAcceptance(): void {
    runInAction(() => {
      this.keyTermsAcceptedAt = null;
      this.keyTermsVersion = null;
    });
  }

  /**
   * Whether the user has accepted the key terms.
   */
  get hasAcceptedKeyTerms(): boolean {
    return this.keyTermsAcceptedAt !== null && this.keyTermsVersion !== null;
  }

  // Order creation
  async createOrder(
    paymentFrequency: string,
    selectedBreakDown: PaymentPlanDetails,
    keepMyPrice: boolean,
    durationMonths: number
  ): Promise<CheckoutSessionResult> {
    try {
      this.isCreatingOrder = true;
      this.setError(null);

      const orderPayload: CheckoutSessionParams = {
        productId: this.paymentPlanResults!.product.id,
        quantity: 1,
        paymentOption: 'PSS',
        autoDebitOptIn: true,
        paymentPlan: {
          durationMonths: durationMonths,
          frequency: paymentFrequency,
          kmpEnabled: keepMyPrice,
        },
        // deliveryOption: '',
        // deliveryAddressId: '',
      };

      const response = await this.checkoutService.createSession(orderPayload);
     
      if (response) {
        // Clear checkout data after successful order creation
        this.clearCheckout();
        runInAction(() => {
          this.checkoutSession = response;
        });
        trackConsumerEvent({
          eventType: 'checkout_started',
          checkoutSessionId: response.sessionId,
          properties: {
            product_id: this.paymentPlanResults?.product?.id || '',
            checkout_session_id: response.sessionId,
            plan_type: paymentFrequency,
            amount: Number(response.paymentBreakdown.amountDueNow || 0),
            currency: response.paymentBreakdown.currency || 'GHS',
          },
        });
        return response;
      } else {
        throw new Error('Failed to create order');
      }
    } catch (error: unknown) {
      this.setError(error instanceof Error ? error.message : 'Failed to create order');
      throw error;
    } finally {
      this.isCreatingOrder = false;
    }
  }

  async loadCheckoutSession(orderId: string): Promise<void> {
    const generation = ++this.checkoutGeneration;
    const accountId = this.container.authStore?.currentUser?.id;
    const epoch = checkoutObservationEpoch();
    const currentIdentity = () => accountId === this.container.authStore?.currentUser?.id && epoch === checkoutObservationEpoch();
    this.checkoutLoadError = null;
    this.clearWalletState();
    this.currentCheckoutSession = null;
    this.createdOrder = null;
    this.isFinalizingOrder = false;
    try {
      this.setLoading(true);
      this.setError(null);
      const response = await this.checkoutService.getSession(orderId);
      if (generation !== this.checkoutGeneration) return;
      if (!currentIdentity() || (accountId && response.account_id !== String(accountId))) {
        runInAction(() => { this.checkoutLoadError = 'sign_in'; });
        return;
      }
      runInAction(() => {
        this.currentCheckoutSession = response;
        if (response.status === 'expired') this.walletConsentError = 'expired';
        const application = response.store_credit_application;
        this.walletApplication = application
          ? { id: application.id, amount: Number(application.amount) }
          : null;
        this.isLoading = false;
        this.setError(null);
      });
      void this.loadWalletBalance();
      if (this.walletApplication || response.status === 'completed') {
        await this.readWalletConsent(orderId, generation);
        if (generation === this.checkoutGeneration) this.resumeWalletConsentPolling();
      }
    } catch (error) {
      if (generation === this.checkoutGeneration) {
        const status = (error as HttpErrorLike)?.response?.status;
        runInAction(() => { this.checkoutLoadError = !currentIdentity() || status === 401 || status === 403 ? 'sign_in' : 'unavailable'; });
        this.setError('Failed to load checkout session');
      }
    } finally {
      if (generation === this.checkoutGeneration) this.setLoading(false);
    }
  }

  get walletConsentPhoneNumber(): string {
    return this.container.paymentStore.getLockedPaymentPhoneNumber(this.container.authStore.userPhoneNumber);
  }

  get walletConsentProvider(): CheckoutConsentProvider | null {
    const consent = this.walletConsent;
    if (consent && ['pending', 'active', 'fallback'].includes(consent.status)) {
      if (consent.provider === 'mtn_momo') return 'mtn';
      if (consent.provider === 'telecel_momo') return 'telecel';
    }
    const options = this.currentCheckoutSession?.available_payment_methods;
    return this.container.paymentStore.getDefaultPssConsentProvider(Array.isArray(options) ? options : []);
  }

  beginWalletCheckout(): Promise<boolean> {
    return this.requestWalletCheckout(false);
  }

  checkWalletCheckout(): Promise<boolean> {
    return this.requestWalletCheckout(true);
  }

  private requestWalletCheckout(manualCheck: boolean): Promise<boolean> {
    if (this.walletConsentRequest) return this.walletConsentRequest;
    const session = this.currentCheckoutSession;
    if (!session) return Promise.resolve(false);
    if (session.status === 'completed' || this.walletConsent?.status === 'completed') {
      return Promise.resolve(true);
    }
    if (this.isSessionCleanupInProgress) {
      this.walletError = 'Please finish signing in before making a payment.';
      return Promise.resolve(false);
    }
    if (!this.walletCoversFullAmount || this.isFinalizingOrder) return Promise.resolve(false);
    this.stopWalletConsentPolling();
    this.isRequestingWalletConsent = true;
    this.walletConsentError = null;
    if (!manualCheck) this.walletConsentPollingPaused = false;
    const generation = this.checkoutGeneration;
    const request = this.performWalletCheckout(session.session_id, generation, manualCheck);
    this.walletConsentRequest = request;
    void request.finally(() => {
      if (this.walletConsentRequest !== request) return;
      runInAction(() => {
        this.walletConsentRequest = null;
        this.isRequestingWalletConsent = false;
        this.resumeWalletConsentPolling();
      });
    });
    return request;
  }

  private isCurrentCheckout(sessionId: string, generation: number): boolean {
    return this.checkoutGeneration === generation && this.currentCheckoutSession?.session_id === sessionId;
  }

  private async performWalletCheckout(sessionId: string, generation: number, manualCheck: boolean): Promise<boolean> {
    const version = ++this.consentRequestVersion;
    try {
      const provider = this.walletConsentProvider;
      const phone = this.walletConsentPhoneNumber;
      if (!provider || !phone) throw new Error('Checkout approval details unavailable');
      const result = await this.checkoutService.requestRecurringConsent(sessionId, {
        payment_provider: provider, phone_number: phone,
      });
      if (!this.isCurrentCheckout(sessionId, generation) || version !== this.consentRequestVersion) return false;
      runInAction(() => { this.walletConsent = result; });
      if (manualCheck && result.status === 'pending') {
        runInAction(() => { this.walletConsentError = 'setup_failed'; });
        return false;
      }
      return await this.finishWalletConsent(result, sessionId, generation);
    } catch (_error) {
      if (this.isCurrentCheckout(sessionId, generation) && version === this.consentRequestVersion) {
        runInAction(() => { this.walletConsentError = 'setup_failed'; });
      }
      return false;
    }
  }

  private async finishWalletConsent(
    result: CheckoutConsentResult, sessionId: string, generation: number,
  ): Promise<boolean> {
    if (!this.isCurrentCheckout(sessionId, generation)) return false;
    if (result.status === 'completed') return true;
    if (result.status === 'expired') {
      runInAction(() => { this.walletConsentError = 'expired'; });
      return false;
    }
    if (result.status === 'required') {
      runInAction(() => { this.walletConsentError = 'setup_failed'; });
      return false;
    }
    if (!result.can_finalize || !['active', 'fallback'].includes(result.status)) return false;
    if (!this.walletCoversFullAmount || this.isApplyingWallet) return false;
    if (this.isSessionCleanupInProgress) {
      runInAction(() => {
        this.walletConsentPollingPaused = true;
        this.walletError = 'Please finish signing in before making a payment.';
      });
      return false;
    }
    try {
      await this.finalizeOrder();
      return this.isCurrentCheckout(sessionId, generation);
    } catch (_error) {
      if (this.isCurrentCheckout(sessionId, generation)) {
        runInAction(() => { this.walletConsentError = 'finalization_failed'; });
      }
      return false;
    }
  }

  private async readWalletConsent(
    sessionId: string, generation: number,
  ): Promise<CheckoutConsentResult | null> {
    const version = ++this.consentRequestVersion;
    try {
      const result = await this.checkoutService.getRecurringConsent(sessionId);
      if (!this.isCurrentCheckout(sessionId, generation) || version !== this.consentRequestVersion) return null;
      runInAction(() => { this.walletConsent = result; });
      return result;
    } catch (_error) {
      if (!this.isCurrentCheckout(sessionId, generation) || version !== this.consentRequestVersion) return null;
      // A status read cannot undo a completed purchase or turn pending approval into rejection.
      const completed = this.currentCheckoutSession?.status === 'completed' || this.walletConsent?.status === 'completed';
      captureMessage(completed ? 'checkout.completed_consent_status_unavailable' : 'checkout.recurring_consent_status_unavailable', {
        level: completed ? 'info' : 'warning',
      });
      return null;
    }
  }

  resumeWalletConsentPolling(): void {
    if (this.currentCheckoutSession?.status === 'completed' || this.walletConsentError || this.walletConsentPollingPaused) return;
    if (this.walletConsent?.status !== 'pending' || this.walletConsentTimer || this.isPollingWalletConsent || this.isRequestingWalletConsent) return;
    this.walletConsentDeadline = Date.now() + 90_000;
    this.scheduleWalletConsentPoll(this.checkoutGeneration, this.walletPollGeneration);
  }

  stopWalletConsentPolling(): void {
    this.walletPollGeneration += 1;
    this.consentRequestVersion += 1;
    if (this.walletConsentTimer) clearTimeout(this.walletConsentTimer);
    this.walletConsentTimer = null;
    this.isPollingWalletConsent = false;
  }

  private scheduleWalletConsentPoll(generation: number, pollGeneration: number): void {
    if (pollGeneration !== this.walletPollGeneration) return;
    if (Date.now() >= this.walletConsentDeadline) {
      this.pauseWalletConsentPolling();
      return;
    }
    this.walletConsentTimer = setTimeout(() => {
      runInAction(() => { this.walletConsentTimer = null; });
      void this.pollWalletConsent(generation, pollGeneration);
    }, 5_000);
  }

  private async pollWalletConsent(generation: number, pollGeneration: number): Promise<void> {
    const sessionId = this.currentCheckoutSession?.session_id;
    if (!sessionId || this.isRequestingWalletConsent || pollGeneration !== this.walletPollGeneration) return;
    if (Date.now() >= this.walletConsentDeadline) {
      this.pauseWalletConsentPolling();
      return;
    }
    runInAction(() => { this.isPollingWalletConsent = true; });
    const result = await this.readWalletConsent(sessionId, generation);
    if (!this.isCurrentCheckout(sessionId, generation) || pollGeneration !== this.walletPollGeneration) return;
    runInAction(() => { this.isPollingWalletConsent = false; });
    if (result && result.status !== 'pending') {
      await this.finishWalletConsent(result, sessionId, generation);
      return;
    }
    if (Date.now() >= this.walletConsentDeadline) {
      this.pauseWalletConsentPolling();
      return;
    }
    this.scheduleWalletConsentPoll(generation, pollGeneration);
  }

  private pauseWalletConsentPolling(): void {
    // Elapsed observation time is not a provider failure or permission to finalize.
    this.stopWalletConsentPolling();
    this.walletConsentPollingPaused = true;
  }

  async listCollectionPoints(region: string): Promise<CollectionPointOption[]> {
    return this.checkoutService.listCollectionPoints({ region });
  }

  /**
   * Loads collection points for a given region and stores them in state.
   * Used by DeliveryPreferences component to populate the collection point dropdown.
   */
  async loadCollectionPointsForRegion(region: string): Promise<void> {
    if (!region) return;

    runInAction(() => {
      this.isLoadingCollectionPoints = true;
      this.collectionPoints = [];
      this.collectionPointsError = null;
    });

    try {
      const points = await this.listCollectionPoints(region);
      runInAction(() => {
        this.collectionPoints = points;
      });
    } catch (error) {
      runInAction(() => {
        this.collectionPoints = [];
        this.collectionPointsError =
          (error as Error).message || 'Failed to load collection points';
      });
    } finally {
      runInAction(() => {
        this.isLoadingCollectionPoints = false;
      });
    }
  }

  /**
   * Finalizes the order after payment is confirmed.
   * Delivery params are optional - if not provided, order is created without delivery preference
   * (user can set delivery preference later when "ready to redeem").
   */
  async finalizeOrder(params?: {
    deliveryMethod?: 'COLLECTION_POINT' | 'HOME_DELIVERY';
    collectionPoint?: { id: string; code: string; name: string; region: string; city?: string };
    deliveryAddressId?: string;
    pickupNote?: string;
    deliveryNote?: string;
  }): Promise<CheckoutOrderResult> {
    if (this.isSessionCleanupInProgress) throw new Error('Please finish signing in before making a payment.');
    if (!this.currentCheckoutSession) {
      throw new Error('No active checkout session');
    }

    const sessionId = this.currentCheckoutSession.session_id;
    const generation = this.checkoutGeneration;
    const paymentOption = (
      this.currentCheckoutSession.selected_plan as SelectedPlanLike | undefined
    )?.payment_option;
    if (paymentOption !== 'PSS') {
      throw new Error('PaySmallSmall is required to create an order');
    }
    const paymentMethod = 'paysmallsmall';

    try {
      runInAction(() => {
        this.isFinalizingOrder = true;
        this.error = null;
      });

      // Build delivery preferences if collection point is provided
      const deliveryPreferences =
        params?.deliveryMethod === 'COLLECTION_POINT' && params?.collectionPoint
          ? {
              collection_point: {
                id: params.collectionPoint.id,
                code: params.collectionPoint.code,
                name: params.collectionPoint.name,
                region: params.collectionPoint.region,
                city: params.collectionPoint.city,
              },
            }
          : undefined;

      const order = await this.checkoutService.createOrderFromCheckoutSession({
        checkoutSessionId: sessionId,
        paymentMethod,
        deliveryMethod: params?.deliveryMethod,
        deliveryAddressId: params?.deliveryAddressId,
        deliveryPreferences,
        pickupNote: params?.pickupNote || null,
        deliveryNote: params?.deliveryNote || null,
      });

      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) {
          this.createdOrder = order;
          this.currentCheckoutSession!.status = 'completed';
          this.walletConsent = {
            status: 'completed', provider: this.walletConsent?.provider ?? null,
            can_finalize: false, auto_debit_active: null,
          };
          this.stopWalletConsentPolling();
        }
      });
      if (this.isCurrentCheckout(sessionId, generation)) await this.readWalletConsent(sessionId, generation);
      trackConsumerEvent({
        eventType: 'order_created',
        orderId: order?.id,
        checkoutSessionId: sessionId,
        properties: {
          order_id: order?.id || '',
          order_number: order?.order_number || '',
          checkout_session_id: sessionId,
          amount: Number(order?.total_amount || 0),
          currency: order?.currency || 'GHS',
        },
      });

      return order;
    } finally {
      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) this.isFinalizingOrder = false;
      });
    }
  }
  
  // Shopping Wallet computed values

  get hasWalletBalance(): boolean {
    return this.walletBalance > 0;
  }

  /**
   * The adjusted amount due now after wallet application.
   * If wallet is applied, subtracts the applied amount from the original amount due now.
   */
  get adjustedAmountDueNow(): number {
    const original = Number(this.currentCheckoutSession?.payment_breakdown?.amount_due_now) || 0;
    if (this.walletApplication) {
      return Math.max(0, original - this.walletApplication.amount);
    }
    return original;
  }

  /**
   * Whether the wallet fully covers the amount due now.
   */
  get walletCoversFullAmount(): boolean {
    if (!this.walletApplication) return false;
    const amountDue = Number(this.currentCheckoutSession?.payment_breakdown?.amount_due_now) || 0;
    return amountDue > 0 && Number.isFinite(this.walletApplication.amount) && this.walletApplication.amount >= amountDue;
  }

  // Shopping Wallet actions

  get walletReadRecovery(): WalletReadFailure | null {
    return this.walletError === 'Failed to load wallet balance' || this.isLoadingWallet
      ? this.walletReadFailure : null;
  }

  async retryWalletBalance(): Promise<void> {
    if (this.isLoadingWallet || this.isApplyingWallet) return;
    await this.loadWalletBalance();
  }

  /**
   * Load wallet balance from the store credit API.
   */
  async loadWalletBalance(): Promise<void> {
    const sessionId = this.currentCheckoutSession?.session_id;
    if (!this.paymentService || !sessionId) return;
    const generation = this.checkoutGeneration;
    const version = ++this.walletBalanceRequestVersion;

    runInAction(() => {
      this.isLoadingWallet = true;
      this.walletError = null;
      this.walletBalance = 0;
    });

    try {
      const data = await this.paymentService.getStoreCreditBalance({ checkout_session_id: sessionId });
      if (version !== this.walletBalanceRequestVersion || !this.isCurrentCheckout(sessionId, generation)) return;
      const paymentOption = (
        this.currentCheckoutSession?.selected_plan as SelectedPlanLike | undefined
      )?.payment_option;
      const contextualBalance = paymentOption === 'PAY_IN_FULL'
        ? data.purchase_available_balance
        : paymentOption === 'PSS'
          ? data.pss_initial_available_balance
          : undefined;
      const balance = Number(contextualBalance);
      if (typeof contextualBalance !== 'string'
        || !Number.isFinite(balance) || balance < 0) {
        throw new Error('Contextual Wallet balance unavailable');
      }
      runInAction(() => {
        this.walletBalance = balance;
        this.walletCurrency = data.currency || 'GHS';
        this.walletCredits = data.credits ?? [];
        this.walletReadFailure = null;
      });
    } catch (error) {
      if (version !== this.walletBalanceRequestVersion || !this.isCurrentCheckout(sessionId, generation)) return;
      runInAction(() => {
        const firstFailure = this.walletReadFailure === null;
        this.walletBalance = 0;
        this.walletCredits = [];
        this.walletError = 'Failed to load wallet balance';
        this.walletReadFailure = classifyWalletReadFailure(error);
        if (firstFailure) {
          captureMessage('Checkout Wallet balance refresh unavailable', {
            level: 'warning', extra: { wallet_read: this.walletReadFailure },
          });
        }
      });
    } finally {
      runInAction(() => {
        if (version === this.walletBalanceRequestVersion && this.isCurrentCheckout(sessionId, generation)) {
          this.isLoadingWallet = false;
        }
      });
    }
  }

  /**
   * Apply wallet credits to the current checkout session.
   */
  async applyWalletToCheckout(sessionId: string, amount?: number): Promise<void> {
    if (this.isSessionCleanupInProgress) {
      this.walletError = 'Please finish signing in before making a payment.';
      return;
    }
    if (!this.paymentService || this.isApplyingWallet) return;
    const generation = this.checkoutGeneration;
    this.stopWalletConsentPolling();

    runInAction(() => {
      this.isApplyingWallet = true;
      this.walletError = null;
    });

    try {
      const result = await this.paymentService.applyStoreCredit(
        sessionId,
        amount?.toFixed(2)
      );
      if (!this.isCurrentCheckout(sessionId, generation)) return;
      runInAction(() => {
        this.walletApplication = {
          id: result.application_id,
          amount: Number(result.amount_applied) || 0,
        };
        this.walletConsent = null;
        this.walletConsentError = null;
        this.walletConsentPollingPaused = false;
        this.walletQuoteNeedsRefresh = true;
      });
      await this.loadWalletBalance();
      await this.refreshWalletCheckoutQuote(sessionId, generation);
    } catch (_error) {
      if (!this.isCurrentCheckout(sessionId, generation)) return;
      runInAction(() => {
        this.walletError = 'Failed to apply wallet';
      });
    } finally {
      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) this.isApplyingWallet = false;
      });
    }
  }

  invalidateWalletQuote(): void {
    this.walletQuoteNeedsRefresh = true;
  }

  async ensureWalletQuoteReady(): Promise<boolean> {
    if (this.isApplyingWallet) return false;
    const session = this.currentCheckoutSession;
    if (!session || ['expired', 'completed', 'resolved'].includes(session.status)) return false;
    if (!this.walletQuoteNeedsRefresh) return true;
    const sessionId = session.session_id;
    const generation = this.checkoutGeneration;
    this.isApplyingWallet = true;
    try {
      return await this.refreshWalletCheckoutQuote(sessionId, generation);
    } finally {
      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) this.isApplyingWallet = false;
      });
    }
  }

  private walletQuoteSnapshot(): string {
    return JSON.stringify([
      this.currentCheckoutSession?.payment_breakdown,
      this.currentCheckoutSession?.selected_plan,
      this.currentCheckoutSession?.available_payment_methods,
      this.walletApplication,
    ]);
  }

  private async refreshWalletCheckoutQuote(sessionId: string, generation: number): Promise<boolean> {
    const previousQuote = this.walletQuoteSnapshot();
    const previousApplication = this.walletApplication;
    try {
      const session = await this.checkoutService.getSession(sessionId);
      if (!this.isCurrentCheckout(sessionId, generation)) return false;
      runInAction(() => {
        this.currentCheckoutSession = session;
        if (session.status === 'expired') this.walletConsentError = 'expired';
        if (session.store_credit_application !== undefined) {
          const application = session.store_credit_application;
          this.walletApplication = application ? { id: application.id, amount: Number(application.amount) } : null;
        }
        this.walletQuoteNeedsRefresh = false;
        if (this.walletError === 'Failed to load checkout session') this.walletError = null;
      });
      runInAction(() => {
        if (previousApplication?.id !== this.walletApplication?.id
          || previousApplication?.amount !== this.walletApplication?.amount) this.walletBalanceNeedsRefresh = true;
      });
      if (this.walletBalanceNeedsRefresh) {
        await this.loadWalletBalance();
        if (!this.isCurrentCheckout(sessionId, generation)) return false;
        runInAction(() => {
          this.walletBalanceNeedsRefresh = this.walletError === 'Failed to load wallet balance';
          this.walletQuoteNeedsRefresh = this.walletBalanceNeedsRefresh;
        });
        return false;
      }
      // A retry that changes the price or funding must return to customer review.
      return !['expired', 'completed', 'resolved'].includes(session.status)
        && previousQuote === this.walletQuoteSnapshot();
    } catch (_error) {
      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) this.walletError = 'Failed to load checkout session';
      });
      return false;
    }
  }

  /**
   * Remove a wallet credit application from checkout.
   */
  async removeWalletApplication(): Promise<void> {
    if (this.isSessionCleanupInProgress) {
      this.walletError = 'Please finish signing in before making a payment.';
      return;
    }
    if (!this.paymentService || !this.walletApplication || this.isApplyingWallet) return;
    const sessionId = this.currentCheckoutSession?.session_id;
    if (!sessionId || this.currentCheckoutSession?.status === 'completed') return;
    const generation = this.checkoutGeneration;
    const applicationId = this.walletApplication.id;
    this.stopWalletConsentPolling();

    runInAction(() => {
      this.isApplyingWallet = true;
      this.walletError = null;
    });

    try {
      await this.paymentService.removeStoreCreditApplication(applicationId);
      if (!this.isCurrentCheckout(sessionId, generation)) return;
      runInAction(() => {
        this.walletApplication = null;
        this.walletConsent = null;
        this.walletConsentError = null;
        this.walletConsentPollingPaused = false;
      });
      await this.loadWalletBalance();
    } catch (_error) {
      if (!this.isCurrentCheckout(sessionId, generation)) return;
      runInAction(() => {
        this.walletError = 'Failed to remove wallet application';
      });
    } finally {
      runInAction(() => {
        if (this.isCurrentCheckout(sessionId, generation)) this.isApplyingWallet = false;
      });
    }
  }

  /**
   * Clear wallet state (e.g., on checkout reset).
   */
  clearWalletState(): void {
    this.stopWalletConsentPolling();
    this.walletBalanceRequestVersion += 1;
    this.isLoadingWallet = false;
    this.consentRequestVersion += 1;
    this.walletConsentRequest = null;
    this.walletConsent = null;
    this.walletConsentError = null;
    this.walletConsentPollingPaused = false;
    this.isRequestingWalletConsent = false;
    this.isApplyingWallet = false;
    this.walletBalance = 0;
    this.walletCredits = [];
    this.walletApplication = null;
    this.walletQuoteNeedsRefresh = false;
    this.walletBalanceNeedsRefresh = false;
    this.walletError = null;
    this.walletReadFailure = null;
  }

  // Validation helpers
  validateWorkDetails(): string[] {
    const errors: string[] = [];

    if (!this.workDetails) {
      return ['Work details are required'];
    }

    if (!this.workDetails.employerName) {
      errors.push('Employer name is required');
    }

    if (!this.workDetails.workAddress) {
      errors.push('Work address is required');
    }

    if (!this.workDetails.salary || this.workDetails.salary <= 0) {
      errors.push('Valid salary is required');
    }

    if (!this.workDetails.workPhone) {
      errors.push('Work phone is required');
    }

    return errors;
  }

  validateShippingAddress(): string[] {
    const errors: string[] = [];

    if (!this.shippingAddress) {
      return ['Shipping address is required'];
    }

    if (!this.shippingAddress.firstName) {
      errors.push('First name is required');
    }

    if (!this.shippingAddress.lastName) {
      errors.push('Last name is required');
    }

    if (!this.shippingAddress.street) {
      errors.push('Street address is required');
    }

    if (!this.shippingAddress.city) {
      errors.push('City is required');
    }

    if (!this.shippingAddress.region) {
      errors.push('Region is required');
    }

    return errors;
  }

  // Reset and cleanup
  clearCheckout(): void {
    this.checkoutGeneration += 1;
    runInAction(() => {
      this.currentCheckoutSession = null;
      this.createdOrder = null;
      this.isFinalizingOrder = false;
      this.items = [];
      this.selectedPaymentPlan = null;
      this.selectedPaymentMethod = null;
      this.workDetails = null;
      this.shippingAddress = null;
      this.termsAccepted = false;
      this.privacyAccepted = false;
      this.marketingOptIn = false;
      this.keyTermsAcceptedAt = null;
      this.keyTermsVersion = null;
      this.error = null;
      this.isLoading = false;
    });
    this.clearWalletState();
    this.initializeDefaultPlans();
  }

  reset(): void {
    this.clearCheckout();
  }

  // Payment plan management
  private initializeDefaultPlans(): void {
    this.availablePaymentPlans = [
      {
        type: PaymentType.FULL_PAYMENT,
        totalAmount: 0, // Will be calculated dynamically
        processingFee: 0,
      },
    ];
  }

  private generateArrayOfDays(
    duration: number,
    intervalDays: number
  ): number[] {
    const days: number[] = [];
    for (let i = 0; i < duration; i++) {
      days.push(i * intervalDays);
    }
    return days;
  }

  private getDurationInDays(duration: number, frequency: string): number {
    switch (frequency.toLowerCase()) {
      case 'daily':
        return duration;
      case 'weekly':
        return duration * 7;
      case 'monthly':
        return duration * 30;
      default:
        return duration * 7;
    }
  }
}

export { CheckoutStore };
